import * as Sentry from "@sentry/angular";
import { AppModule } from "./app/app.module";
import { enableProdMode } from "@angular/core";
import { environment } from "./environments/environment";
import { hmrBootstrap } from "./hmr";
import { platformBrowserDynamic } from "@angular/platform-browser-dynamic";
import { Constants } from "./constants";

Sentry.init({
    dsn: Constants.sentry.dsn,
    environment: Constants.sentry.environment,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: Constants.sentry.tracesSampleRate,
    tracePropagationTargets: Constants.sentry.tracePropagationTargets,
    replaysSessionSampleRate: Constants.sentry.replaysSessionSampleRate,
    replaysOnErrorSampleRate: Constants.sentry.replaysOnErrorSampleRate,
    sendDefaultPii: Constants.sentry.sendDefaultPii,
    enableLogs: Constants.sentry.enableLogs,
    enabled: Constants.sentry.enabled,
    debug: Constants.sentry.debug,
    release: `${environment.appVersion}`,
    ignoreErrors: [
        "NetworkError",
        "Failed to fetch",
        "Load failed",
        "The network connection was lost",
        /Loading chunk [\d]+ failed/,
        /ChunkLoadError/,
        // WebKit/Safari's wording for the same stale-deployed-chunk failure as ChunkLoadError above —
        // the user has an old page open and the hashed chunk it references no longer exists post-deploy.
        "Importing a module script failed",
        // iOS/Chrome Mobile iOS (also WebKit) wording for the same stale-deployed-chunk failure: the
        // 404 for the missing chunk returns the SPA's index.html fallback (text/html) instead of JS,
        // and the module loader complains about the MIME type rather than the missing script.
        "'text/html' is not a valid JavaScript MIME type.",
        // Firefox's wording for the same stale-deployed-chunk failure: it does fetch the fallback
        // index.html body and tries to parse it as a script, choking on the leading "<!doctype html>".
        "expected expression, got '<'",
        // Our own ErrorInterceptorService rethrows a bare "timeout" string for client-side request
        // timeouts (see errorInterceptor.service.ts); if a caller doesn't .catch() it, it surfaces here
        // as an unhandled rejection. Already reported with proper context via that interceptor.
        /^timeout$/i,
        "ResizeObserver loop limit exceeded",
        "ResizeObserver loop completed with undelivered notifications",
        "Non-Error exception captured",
        "Non-Error promise rejection captured",
    ],
    denyUrls: [/extensions\//i, /^chrome:\/\//i, /^chrome-extension:\/\//i, /^moz-extension:\/\//i],
    beforeSend(event) {
        // Drop login_required exceptions — handled gracefully in-app and captured as
        // info-level messages via ArcusAuthService.handleSessionExpired() to avoid noise.
        const errorValue = event.exception?.values?.[0]?.value;
        if (errorValue === "Login required" || errorValue === "login_required") return null;
        if (errorValue === "Multifactor authentication required" || errorValue === "mfa_required") return null;
        // Auth0 hidden-iframe token refresh timeout — handled via ArcusAuthService.handleSessionExpired().
        if (errorValue === "Timeout" || errorValue === "timeout") return null;
        if (errorValue === "consent_required") return null;

        const frames = event.exception?.values?.[0]?.stacktrace?.frames;
        if (!frames || frames.length === 0) return null;
        return event;
    },
});

// Session Replay's rrweb-based recording code is the single largest piece of the Sentry SDK
// (~600 KB raw). Loading it eagerly in the init() call above blocks first paint on cold-cache
// mobile visits — flagged by Sentry itself as a "Large Render Blocking Asset" on the anonymous
// carrier-facing routes, which are usually a user's very first pageview with nothing cached.
//
// A dynamic import() here does NOT defer it: @sentry/angular is also imported synchronously
// elsewhere at root level (errorInterceptor.service.ts, arcusauth.service.ts, user.service.ts),
// so the bundler resolves the "dynamic" import against the copy already being loaded eagerly —
// nothing actually gets split into a separate chunk. Sentry.lazyLoadIntegration() sidesteps this
// by fetching Replay from Sentry's CDN via an injected <script> tag, outside our own bundle
// graph entirely, so it genuinely can't be pulled back into the eager chunk. Tradeoff: errors
// during the first few seconds of a session (before idle/timeout fires) won't have a replay
// attached, and this adds a runtime dependency on browser.sentry-cdn.com being reachable.
if (Constants.sentry.replaysSessionSampleRate > 0 || Constants.sentry.replaysOnErrorSampleRate > 0) {
    const loadReplay = () =>
        Sentry.lazyLoadIntegration("replayIntegration")
            .then((replayIntegration) => Sentry.addIntegration(replayIntegration()))
            .catch(() => {});

    if ("requestIdleCallback" in window) {
        requestIdleCallback(loadReplay, { timeout: 5000 });
    } else {
        setTimeout(loadReplay, 3000);
    }
}

// Some WebKit/iOS browsers (notably in-app browsers opened from email/SMS links) throw
// DataCloneError from history.pushState/replaceState for state objects other browsers clone
// fine. This is a browser quirk outside our control, not an app bug — retry with no state so
// navigation still completes instead of crashing the current router navigation.
(["pushState", "replaceState"] as const).forEach((method) => {
    const original = history[method].bind(history);
    history[method] = (state: any, title: string, url?: string | URL | null) => {
        try {
            return original(state, title, url);
        } catch (e) {
            if (e instanceof DOMException && e.name === "DataCloneError") {
                return original(null, title, url);
            }
            throw e;
        }
    };
});

export function getBaseUrl() {
    return document.getElementsByTagName("base")[0].href;
}

const providers = [{ provide: "BASE_URL", useFactory: getBaseUrl, deps: [] }];

if (environment.production) {
    enableProdMode();
}

const bootstrap = () => platformBrowserDynamic(providers).bootstrapModule(AppModule);

if (environment.hmr) {
    if (module["hot"]) {
        hmrBootstrap(module, bootstrap);
    } else {
        console.error("HMR is not enabled for webpack-dev-server!");
        console.log("Are you using the --hmr flag for ng serve?");
    }
} else {
    bootstrap().catch((err) => console.log(err));
}
